import { useState, useMemo, ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { FormGroup, Box, FormHelperText } from '@mui/material';

import {
  getPluralAreaUnit,
  getShortAreaUnit,
} from '../../../../../shared/features/areaUnit/helpers/functions/areaUnit';
import {
  formatNumber,
  isNumber,
} from '../../../../../shared/helpers/functions/number';
import { MIN_ANNUAL_AREA_AMOUNTS } from '../../../../../shared/helpers/constants/areaAmounts';
import rootStore from '../../../rootStore/RootStore';
import useStripeSubscriptionPreview from '../../../subscriptionPreview/hooks/useStripeSubscriptionPreview';
import { getDebouncer } from '../../../../../shared/helpers/functions/debouncer';
import TextField from '../../../../../shared/components/TextField';
import usePriceConfig from '../../../../../shared/features/priceConfig/hooks/usePriceConfig';
import useTotalUsedArea from '../../../../../shared/features/user/hooks/useTotalUsedArea';
import useTotalUsedSeats from '../../../../../shared/features/user/hooks/useTotalUsedSeats';
import {
  MAX_INPUT_VALUE,
  MIN_INPUT_VALUE,
} from '../../../../../shared/helpers/constants/ui';

const generatePreviewDebouncer = getDebouncer(500);

const AnnualSubscriptionForm = observer(() => {
  const { t } = useTranslation();
  const { generateSubscriptionPreview } = useStripeSubscriptionPreview();
  const { isLoaded: priceConfigLoaded } = usePriceConfig(rootStore);
  const {
    uiStateStore,
    checkoutStore,
    areaUnitStore,
    subscriptionPreviewStore,
  } = rootStore;
  const { loaded: totalUsedAreaLoaded, totalUsedArea } =
    useTotalUsedArea(rootStore);
  const { loaded: totalUsedSeatsLoaded, totalUsedSeats } =
    useTotalUsedSeats(rootStore);
  const {
    annualPlan,
    setAnnualPlanArea,
    setAnnualPlanSeats,
    setAnnualPlanPlanetImagery,
  } = uiStateStore;
  const { areaUnit } = areaUnitStore;
  const { subscriptionPreviewError } = subscriptionPreviewStore;

  const minAreaAmount = MIN_ANNUAL_AREA_AMOUNTS[areaUnit];

  const [areaTouched, setAreaTouched] = useState(false);
  const [seatsTouched, setSeatsTouched] = useState(false);

  const effectiveArea = useMemo(() => {
    if (areaTouched) {
      return annualPlan.area;
    }

    if (totalUsedAreaLoaded && totalUsedArea > 0) {
      return Math.ceil(Math.max(minAreaAmount, totalUsedArea)).toString();
    }

    return annualPlan.area;
  }, [
    areaTouched,
    annualPlan.area,
    totalUsedAreaLoaded,
    totalUsedArea,
    minAreaAmount,
  ]);

  const effectiveSeats = useMemo(() => {
    if (seatsTouched) {
      return annualPlan.additionalSeats;
    }

    if (totalUsedSeatsLoaded && totalUsedSeats > 0) {
      return totalUsedSeats.toString();
    }

    return annualPlan.additionalSeats;
  }, [
    seatsTouched,
    annualPlan.additionalSeats,
    totalUsedSeatsLoaded,
    totalUsedSeats,
  ]);

  const areaHasWarning =
    totalUsedAreaLoaded &&
    totalUsedArea > 0 &&
    parseInt(effectiveArea, 10) < totalUsedArea;
  const seatsHasWarning =
    totalUsedSeatsLoaded &&
    totalUsedSeats > 0 &&
    parseInt(effectiveSeats, 10) < totalUsedSeats;

  const formHelperText = useMemo(() => {
    const value = parseInt(effectiveArea, 10);

    if (subscriptionPreviewError) {
      return {
        type: 'error' as const,
        text: t('checkout.helper-text.unable-to-generate-preview'),
      };
    }

    if (value < minAreaAmount) {
      return {
        type: 'error' as const,
        text: t('checkout.helper-text.min-area', {
          amount: minAreaAmount,
          areaUnit: getPluralAreaUnit(areaUnit),
        }),
      };
    }

    if (areaHasWarning && seatsHasWarning) {
      return {
        type: 'warn' as const,
        text: t('checkout.helper-text.total-used-area-and-seats', {
          amount: formatNumber(totalUsedArea),
          areaUnit: getShortAreaUnit(areaUnit),
          count: totalUsedSeats,
        }),
      };
    }

    if (areaHasWarning) {
      return {
        type: 'warn' as const,
        text: t('checkout.helper-text.total-used-area', {
          amount: formatNumber(totalUsedArea),
          areaUnit: getShortAreaUnit(areaUnit),
        }),
      };
    }

    if (seatsHasWarning) {
      return {
        type: 'warn' as const,
        text: t('checkout.helper-text.total-used-seats', {
          count: totalUsedSeats,
        }),
      };
    }

    return null;
  }, [
    t,
    minAreaAmount,
    areaUnit,
    effectiveArea,
    subscriptionPreviewError,
    areaHasWarning,
    seatsHasWarning,
    totalUsedArea,
    totalUsedSeats,
  ]);

  const isQuantityInputValid = (value: number) =>
    !isNumber(value) || (MIN_INPUT_VALUE <= value && value <= MAX_INPUT_VALUE);

  const requestPreview = ({
    area,
    seats,
    planet,
  }: {
    area: string;
    seats: string;
    planet: string;
  }) => {
    if (!uiStateStore.annualPlanValid) {
      return;
    }

    generatePreviewDebouncer(() => {
      void generateSubscriptionPreview({
        annual: { area, seats, planet },
        promocode: checkoutStore.promocode,
      });
    });
  };

  useEffect(() => {
    if (priceConfigLoaded && totalUsedAreaLoaded && totalUsedSeatsLoaded) {
      requestPreview({
        area: effectiveArea,
        seats: effectiveSeats,
        planet: annualPlan.planetImagery,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    priceConfigLoaded,
    totalUsedAreaLoaded,
    totalUsedSeatsLoaded,
    effectiveArea,
    effectiveSeats,
  ]);

  const handleAreaInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);

    if (!isQuantityInputValid(value)) {
      return;
    }

    setAreaTouched(true);

    if (isNumber(value)) {
      setAnnualPlanArea(value.toString());
      requestPreview({
        area: value.toString(),
        seats: effectiveSeats,
        planet: annualPlan.planetImagery,
      });
    } else {
      setAnnualPlanArea('');
    }
  };

  const handleUserSeatsInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);

    if (!isQuantityInputValid(value)) {
      return;
    }

    setSeatsTouched(true);

    if (isNumber(value)) {
      setAnnualPlanSeats(value.toString());
      requestPreview({
        area: effectiveArea,
        seats: value.toString(),
        planet: annualPlan.planetImagery,
      });
    } else {
      setAnnualPlanSeats('');
    }
  };

  const handlePlanetInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);

    if (!isQuantityInputValid(value)) {
      return;
    }

    if (isNumber(value)) {
      setAnnualPlanPlanetImagery(value.toString());
      requestPreview({
        area: effectiveArea,
        seats: effectiveSeats,
        planet: value.toString(),
      });
    } else {
      setAnnualPlanPlanetImagery('');
    }
  };

  return (
    <FormGroup>
      <Box
        sx={{
          display: 'flex',
          columnGap: 3,
        }}
      >
        <TextField
          label={t('checkout.area-amount', {
            areaUnit: getShortAreaUnit(areaUnit),
          })}
          value={effectiveArea}
          error={areaHasWarning}
          inputProps={{
            type: 'number',
            min: minAreaAmount,
            max: MAX_INPUT_VALUE,
          }}
          onChange={handleAreaInputChange}
        />
        <TextField
          label={t('checkout.user-seats')}
          value={effectiveSeats}
          error={seatsHasWarning}
          inputProps={{ type: 'number', min: 0, max: MAX_INPUT_VALUE }}
          onChange={handleUserSeatsInputChange}
        />
        <TextField
          label={t('checkout.planet-imagery-amount', {
            areaUnit: getShortAreaUnit(areaUnit),
          })}
          value={annualPlan.planetImagery}
          inputProps={{ type: 'number', min: 0, max: MAX_INPUT_VALUE }}
          onChange={handlePlanetInputChange}
        />
      </Box>
      <FormHelperText
        sx={(theme) => ({
          width: 1,
          marginTop: 1.5,
          color:
            formHelperText?.type === 'error'
              ? theme.palette.error.main
              : theme.palette.warning.main,
        })}
      >
        {formHelperText?.text}
      </FormHelperText>
    </FormGroup>
  );
});

export default AnnualSubscriptionForm;
